/**
 * CaseTrackUS - Immigration Case Tracking Platform
 * -------------------------------------------------
 * Module: src/services/api.js
 * Purpose: API service layer — all HTTP requests to the backend.
 *
 * Author: Irina Panferova
 * Company: Panferov LLC
 * Website: https://casetrackus.com
 * Created: 2025
 * Updated: 2026
 * Copyright: (c) 2026 Panferov LLC. All rights reserved.
 */

import axios from 'axios';

// ─────────────────────────────────────
// API Instance
// ─────────────────────────────────────

const API_URL = 'https://casetrackus.com';

const api = axios.create({ baseURL: API_URL });

// Add JWT token to every request automatically
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.params = { ...config.params, token };
  }
  return config;
});

// ─────────────────────────────────────
// Auth Endpoints
// ─────────────────────────────────────

export const login = (email, password) =>
  api.post('/auth/login', { email, password });

export const register = (email, password, full_name, account_type = 'individual') =>
  api.post('/auth/register', { email, password, full_name, account_type });

export const getMe = () => api.get('/auth/me');

export const updateMe = (data) => api.put('/auth/me', data);

// ─────────────────────────────────────
// Case Endpoints
// ─────────────────────────────────────

export const getCases = () => api.get('/cases');

/**
 * Creates a new case — USCIS or EOIR.
 *
 * For USCIS: pass { case_type: 'uscis', receipt_number, description, family_member_id }
 * For EOIR:  pass { case_type: 'eoir', eoir_alien_number, eoir_nationality_code,
 *                   eoir_nationality_label, description, family_member_id }
 */
export const createCase = (caseData) => api.post('/cases', caseData);

export const checkCase = (case_id, captchaToken = null) => {
  const headers = {};
  if (captchaToken) {
    headers['X-Captcha-Token'] = captchaToken;
  }
  return api.post(`/cases/${case_id}/check`, {}, { headers });
};

export const deleteCase = (case_id) => api.delete(`/cases/${case_id}`);

export const getCaseHistory = (case_id) => api.get(`/cases/${case_id}/history`);

// ─────────────────────────────────────
// Family Member Endpoints
// ─────────────────────────────────────

export const getFamilyMembers = () => api.get('/family');

export const createFamilyMember = (data) => api.post('/family', data);

export const updateFamilyMember = (memberId, data) => api.put(`/family/${memberId}`, data);

export const deleteFamilyMember = (memberId) => api.delete(`/family/${memberId}`);

export default api;